/**
 * BYO v2 — package list normalization at the API boundary.
 *
 * ## The separator "discrepancy" (was a bug, not a real dual-format contract)
 * CORRECTED 2026-08-19: there is only ONE real backend consumer of
 * `code_config.packages` — `RayDryRunner.scala:232` — and it always splits on
 * **comma**, whether the container was just created inline for a dry-run or
 * loaded from a previously-persisted source. There is no separate
 * newline-based "persistence" format; `byo/core/model.ts`'s `serializePackages`
 * previously joined with a newline by mistake, which silently broke any
 * install of 2+ packages. Both `serializePackages` and this module now
 * comma-join consistently — see `docs/byo/byo-v2-review-2026-08-19.md`.
 *
 * ## Canonical internal form
 * The canonical internal representation everywhere in byo/v2 is a **clean
 * `string[]`** (one bare-or-pinned requirement per element). We only convert to
 * a string at the boundary. `parsePackages`/`serializePackages` are bracket-depth
 * aware so a comma inside pip "extras" syntax (`pkg[extra1,extra2]`) is not
 * treated as a requirement separator — see model.ts for the caveat this does
 * NOT (and cannot, frontend-only) fix: the backend's own split is not
 * bracket-aware, so an extras-with-comma requirement combined with any other
 * requirement in the same request will still be misread server-side.
 */

import { parsePackages, serializePackages } from 'new/components/flows/types/byo/core/model';

/** Re-export the core list normalizers so v2 consumers have one import surface. */
export { parsePackages, serializePackages };

/**
 * True when a requirement's own pip "extras" contain a comma (e.g.
 * `pkg[extra1,extra2]`) — combining it with any other requirement in the same
 * comma-joined request will be misread by the backend's non-bracket-aware
 * split. There is no frontend-only fix for this; surface it as a warning
 * instead of silently sending a value the backend cannot parse correctly.
 */
export function hasUnsafeExtrasComma(pkg: string): boolean {
  const match = pkg.match(/\[([^\]]*)\]/);
  return !!match && match[1].includes(',');
}
